import { useEffect } from 'react'

import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import {
	useRouteError,
	isRouteErrorResponse,
	useNavigate,
} from 'react-router-dom'

import { Button } from '@/shared/components/atoms/Button'
import { H1 } from '@/shared/components/atoms/H1'
import { Logo } from '@/shared/components/atoms/Logo'

export function RouterErrorBoundary() {
	const error = useRouteError()
	const { t } = useTranslation()
	const navigate = useNavigate()

	const softResetWindowMs = 2 * 60 * 1000
	const softResetLimit = 20
	const softResetStorageKey = 'dom_mutation_soft_reset_timestamps'
	const softResetLastDispatchKey = 'dom_mutation_soft_reset_last_dispatch'
	const softResetDebounceMs = 1000

	let errorMessage: string
	let errorStatus: number | undefined
	let stackTrace: string | undefined

	// Check for specific DOM mutation errors
	const errorText = error instanceof Error ? error.message : String(error)
	const domMutationType = errorText.includes(
		"Failed to execute 'insertBefore' on 'Node'"
	)
		? 'insert_before'
		: errorText.includes("Failed to execute 'removeChild' on 'Node'")
			? 'remove_child'
			: undefined
	const isDomMutationError = Boolean(domMutationType)

	useEffect(() => {
		if (isDomMutationError) {
			const now = Date.now()
			let timestamps: number[] = []
			let lastDispatch = 0

			try {
				const stored = sessionStorage.getItem(softResetStorageKey)
				timestamps = stored ? (JSON.parse(stored) as number[]) : []
				lastDispatch = Number(
					sessionStorage.getItem(softResetLastDispatchKey) || 0
				)
			} catch {
				timestamps = []
				lastDispatch = 0
			}

			if (now - lastDispatch < softResetDebounceMs) {
				console.log(
					`Soft reset debounced (${now - lastDispatch}ms since last dispatch)`
				)
				return
			}

			const recent = timestamps.filter(
				(timestamp) => now - timestamp < softResetWindowMs
			)

			if (recent.length < softResetLimit) {
				recent.push(now)
				sessionStorage.setItem(softResetStorageKey, JSON.stringify(recent))
				sessionStorage.setItem(softResetLastDispatchKey, String(now))
				console.log(
					`Dispatching soft reset ${recent.length}/${softResetLimit} in last ${softResetWindowMs}ms`
				)
				window.dispatchEvent(new Event('app:soft-reset'))
			} else {
				console.log(
					`Soft reset limit reached (${softResetLimit} in ${softResetWindowMs}ms)`
				)
			}
		}
	}, [isDomMutationError])

	if (isRouteErrorResponse(error)) {
		errorMessage = error.statusText
		errorStatus = error.status
	} else if (error instanceof Error) {
		errorMessage = error.message
		stackTrace = error.stack

		const sentryTags: Record<string, string> = {
			error_type: 'router_error',
		}
		if (domMutationType) {
			sentryTags.dom_mutation = domMutationType
		}

		// Capture the error in Sentry
		Sentry.captureException(error, {
			tags: sentryTags,
		})
	} else if (typeof error === 'string') {
		errorMessage = error
		Sentry.captureMessage(error, 'error')
	} else {
		console.error(error)
		errorMessage = 'Unknown error'
		Sentry.captureException(new Error('Unknown router error'), {
			extra: {
				error,
			},
		})
	}

	const handleReload = () => {
		window.location.reload()
	}

	const handleGoHome = () => {
		navigate('/zones')
	}

	// Always print the stacktrace into the console log
	console.log('Stacktrace:', stackTrace)

	return (
		<div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background text-foreground">
			<div className="flex flex-col items-center max-w-2xl w-full space-y-8 text-center">
				<div className="mb-4">
					<Logo width="300px" height="45px" />
				</div>

				<div className="space-y-4">
					<H1 className="text-4xl md:text-6xl font-bold">
						{errorStatus
							? `${errorStatus}`
							: t('common.error_page.title', 'Oops!')}
					</H1>
					<p className="text-xl text-muted-foreground mt-4">
						{errorStatus === 404
							? t('common.error_page.not_found', "This page doesn't exist.")
							: t(
									'common.error_page.description',
									'Something went wrong on our side.'
								)}
					</p>

					{errorMessage &&
						!isDomMutationError &&
						process.env.NODE_ENV !== 'production' && (
							<div className="p-4 bg-secondary/50 rounded-lg border border-border mt-2">
								<p className="font-mono text-sm break-all">{errorMessage}</p>
							</div>
						)}
				</div>

				<div className="flex flex-col sm:flex-row gap-4">
					<Button onClick={handleReload} size="lg" className="min-w-[150px]">
						{t('common.error_page.reload', 'Reload Page')}
					</Button>
					<Button
						onClick={handleGoHome}
						variant="outline"
						size="lg"
						className="min-w-[150px]"
					>
						{t('common.error_page.go_home', 'Go Home')}
					</Button>
				</div>

				{process.env.NODE_ENV === 'development' && stackTrace && (
					<div className="w-full mt-12 p-4 bg-black/10 rounded-lg overflow-auto text-left">
						<p className="font-bold mb-2 text-xs uppercase text-muted-foreground">
							Stack Trace (Dev Only)
						</p>
						<pre className="text-xs font-mono opacity-70 whitespace-pre-wrap">
							{stackTrace}
						</pre>
					</div>
				)}
			</div>
		</div>
	)
}
